/**
 * The landing page: what is waiting for you (CLAUDE.md 14.3, 2.2).
 *
 * Section 14.3 says mobile is **review-first** -- "browse projects, read results and
 * utilisations, submit/check/approve". `/` redirected to the project list, so a
 * checker opening the application on a phone was shown a list of folders and had to
 * remember which project held the calculation somebody was waiting on. The review
 * queue is the reason this page exists; the rest is context around it.
 *
 * The distinction the queue is careful about is section 2.2's whole point: a user
 * cannot check their own calculation unless the workspace allows it. A row of your
 * own work in a workspace without self-check is **not** work you can do -- it is work
 * waiting for somebody else. Rendering it as an action item would quietly teach the
 * engineer that they are the reviewer of record, which is precisely the belief the
 * calculated-by / checked-by workflow exists to prevent.
 */
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  ClipboardCheck,
  Clock,
  FileSpreadsheet,
  FolderTree,
  Hourglass,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useHomeFeed } from '@/api/home'
import type { HomePosition } from '@/api/types'
import { useAuth } from '@/auth/AuthProvider'
import { WorkspaceChrome } from '@/components/layout/WorkspaceChrome'
import { Card, CardBody } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { StatusBadge, type Status } from '@/components/ui/StatusBadge'
import { cn } from '@/lib/cn'
import { formatRelative, formatUtilisation } from '@/lib/format'

const REVIEW_KEYS: Record<HomePosition['review_state'], string> = {
  none: 'review.state.none',
  submitted: 'review.state.submitted',
  checked: 'review.state.checked',
  approved: 'review.state.approved',
}

/** Position status -> the badge's vocabulary. */
function badgeStatus(status: string): Status {
  if (status === 'pass') return 'pass'
  if (status === 'fail') return 'fail'
  if (status === 'stale' || status === 'error') return 'warning'
  return 'not_applicable'
}

function PositionRow({
  position,
  /** True when this row is the viewer's own work that they may not check (2.2). */
  waitingOnSomeoneElse = false,
}: {
  position: HomePosition
  waitingOnSomeoneElse?: boolean
}) {
  const { t, i18n } = useTranslation()

  return (
    <li>
      <Card
        className={cn(
          'p-3',
          // A row nobody here can act on reads as context, not as a task.
          waitingOnSomeoneElse && 'opacity-90',
        )}
        data-testid={waitingOnSomeoneElse ? 'queue-row-waiting' : 'queue-row'}
      >
        <Link
          to={`/positions/${position.id}`}
          className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3"
        >
          <span className="flex min-w-0 items-center gap-2">
            <span className="rounded bg-surface-raised px-1.5 py-0.5 text-xs font-medium">
              {position.short_code}
            </span>
            <span className="tabular shrink-0 text-xs text-text-muted">
              {`P${String(position.seq).padStart(2, '0')}`}
            </span>
            <span className="min-w-0 flex-1 truncate font-medium hover:text-accent">
              {position.name}
            </span>
          </span>

          <span className="min-w-0 truncate text-xs text-text-muted sm:flex-1">
            {position.project_name}
            {position.updated_at ? ` · ${formatRelative(position.updated_at, i18n.language)}` : ''}
          </span>

          <span className="flex flex-wrap items-center gap-2">
            {position.governing_ratio !== null && (
              <span className="tabular text-sm text-text-muted">
                {formatUtilisation(position.governing_ratio)}
              </span>
            )}

            {waitingOnSomeoneElse ? (
              // Never colour alone: an icon and a sentence, not a tinted row (14.2).
              <span className="inline-flex items-center gap-1.5 rounded bg-[var(--status-warn-bg)] px-2 py-1 text-xs font-medium text-[var(--status-warn)]">
                <Hourglass size={13} aria-hidden />
                {t('home.queue.waiting_other')}
              </span>
            ) : (
              <span className="rounded bg-surface-raised px-1.5 py-0.5 text-xs text-text-muted">
                {t(REVIEW_KEYS[position.review_state])}
              </span>
            )}

            <StatusBadge status={badgeStatus(position.status)} />
          </span>
        </Link>

        {waitingOnSomeoneElse && (
          <p className="mt-2 text-xs text-text-muted">{t('home.queue.waiting_other_hint')}</p>
        )}
      </Card>
    </li>
  )
}

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof ClipboardCheck
  title: string
  children: ReactNode
}) {
  return (
    <section className="space-y-2">
      <h2 className="flex items-center gap-2 text-sm font-semibold">
        <Icon size={15} className="text-text-muted" aria-hidden />
        {title}
      </h2>
      {children}
    </section>
  )
}

export function HomePage() {
  const { t, i18n } = useTranslation(['common', 'sheets'])
  const { workspaceId } = useAuth()
  const { data, isLoading } = useHomeFeed(workspaceId)

  const queue = data?.review_queue ?? []
  const recent = data?.recent_positions ?? []
  const sheets = data?.recent_sheets ?? []
  const allowSelfCheck = data?.allow_self_check ?? false

  return (
    <WorkspaceChrome title={t('home.title')}>
      <div className="mx-auto max-w-5xl space-y-6 p-4">
        {isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        )}

        {!isLoading && data && (
          <>
            <Section icon={ClipboardCheck} title={t('home.queue.title')}>
              {queue.length === 0 ? (
                <Card>
                  <CardBody className="py-8 text-center">
                    <p className="font-medium">{t('home.queue.empty')}</p>
                    <p className="mt-1 text-sm text-text-muted">{t('home.queue.empty_hint')}</p>
                  </CardBody>
                </Card>
              ) : (
                <ul className="space-y-2">
                  {queue.map((position) => (
                    <PositionRow
                      key={position.id}
                      position={position}
                      waitingOnSomeoneElse={position.own_work && !allowSelfCheck}
                    />
                  ))}
                </ul>
              )}
            </Section>

            <Section icon={Clock} title={t('home.recent.title')}>
              {recent.length === 0 ? (
                <Card>
                  <CardBody className="py-8 text-center">
                    <p className="text-sm text-text-muted">{t('empty.no_positions')}</p>
                  </CardBody>
                </Card>
              ) : (
                <ul className="space-y-2">
                  {recent.map((position) => (
                    <PositionRow key={position.id} position={position} />
                  ))}
                </ul>
              )}
            </Section>

            <Section icon={FileSpreadsheet} title={t('home.sheets.title')}>
              {/* The firewall marker, in the same words and the same styling the
                  Sheets section uses. A sheet listed beside verified module output
                  with nothing to distinguish it is exactly the confusion section 1.3
                  forbids. */}
              <p className="inline-flex items-center gap-2 rounded-md border border-[var(--status-warn)] bg-[var(--status-warn-bg)] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--status-warn)]">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {t('sheets:sheets.unverified.badge')}
              </p>

              {sheets.length === 0 ? (
                <Card>
                  <CardBody className="py-8 text-center">
                    <p className="text-sm text-text-muted">{t('sheets:sheets.empty.title')}</p>
                  </CardBody>
                </Card>
              ) : (
                <ul className="space-y-2">
                  {sheets.map((sheet) => (
                    <li key={sheet.id}>
                      <Card className="p-3">
                        <Link
                          to={`/sheets/${sheet.id}`}
                          className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3"
                        >
                          <span className="min-w-0 flex-1 truncate font-medium hover:text-accent">
                            {sheet.title || t('sheets:sheets.untitled')}
                          </span>
                          <span className="text-xs text-text-muted">
                            {formatRelative(sheet.updated_at, i18n.language)}
                          </span>
                        </Link>
                      </Card>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <div className="flex flex-wrap gap-2 text-sm text-text-muted">
              <Link
                to="/projects"
                className="inline-flex min-h-[2.75rem] items-center gap-2 rounded-md border border-border px-3 hover:bg-surface-raised"
              >
                <FolderTree size={15} aria-hidden />
                {t('home.counts.projects', { count: data.project_count })}
              </Link>
              <span className="inline-flex min-h-[2.75rem] items-center gap-2 rounded-md border border-border px-3">
                {t('home.counts.positions', { count: data.position_count })}
              </span>
            </div>
          </>
        )}
      </div>
    </WorkspaceChrome>
  )
}
